import React, { useEffect, useState } from "react";
const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

export default function CockpitWallets() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crediting, setCrediting] = useState(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/v2/cockpit/wallets`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => { if (d.ok) setUsers(d.users || []); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const credit = async (userId) => {
    if (!amount || parseFloat(amount) <= 0) return;
    setSaving(true); setResult(null);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/wallets/${userId}/credit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount: parseFloat(amount), currency, reason: reason || "Admin credit" })
      });
      const d = await res.json();
      setResult(d);
      if (d.ok) { setCrediting(null); setAmount(""); setReason(""); load(); }
    } catch(e) { setResult({ ok: false, error: e.message }); }
    finally { setSaving(false); }
  };

  const getWalletBalance = (wallets, cur) => {
    const w = (wallets || []).find(w => w.currency === cur);
    return w ? parseFloat(w.balance).toFixed(2) : "0.00";
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">💰 Wallet Management</h1>
        <p className="text-gray-500 text-sm">View and manually credit user wallets · Use for demo funding and customer support</p>
      </div>

      {result && (
        <div className={"p-4 rounded-lg border text-sm " + (result.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700")}>
          {result.ok ? "✅ " + result.message : "❌ " + result.error}
          {result.ok && result.wallet && (
            <span className="ml-2 font-medium">
              ${result.wallet.previousBalance.toFixed(2)} → ${result.wallet.newBalance.toFixed(2)} {result.wallet.currency}
            </span>
          )}
        </div>
      )}

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {["User","Role","CAD Balance","USD Balance","SNPL Limit","SNPL Available","Action"].map(h => (
                <th key={h} className="px-4 py-3 text-left text-gray-600 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>}
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={"px-2 py-0.5 rounded text-xs font-medium " +
                    (u.role === "super_admin" ? "bg-purple-100 text-purple-700" :
                     u.role === "ops_admin" ? "bg-blue-100 text-blue-700" :
                     "bg-gray-100 text-gray-600")}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 font-bold text-gray-800">
                  ${getWalletBalance(u.wallets, "CAD")}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  ${getWalletBalance(u.wallets, "USD")}
                </td>
                <td className="px-4 py-3">${parseFloat(u.snplLimit || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={parseFloat(u.snplAvailable) < 50 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                    ${parseFloat(u.snplAvailable || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {crediting === u.id ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="relative">
                        <span className="absolute left-2 top-1.5 text-gray-400 text-sm">$</span>
                        <input type="number" min="1" step="0.01" value={amount}
                          onChange={e => setAmount(e.target.value)} placeholder="100.00"
                          className="w-24 border rounded-lg pl-5 pr-2 py-1.5 text-sm" />
                      </div>
                      <select value={currency} onChange={e => setCurrency(e.target.value)}
                        className="border rounded-lg px-2 py-1.5 text-sm">
                        <option>CAD</option>
                        <option>USD</option>
                      </select>
                      <input type="text" value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="Reason..." className="border rounded-lg px-2 py-1.5 text-sm w-28" />
                      <button onClick={() => credit(u.id)} disabled={saving}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                        {saving ? "..." : "Credit"}
                      </button>
                      <button onClick={() => { setCrediting(null); setAmount(""); setResult(null); }}
                        className="bg-gray-200 text-gray-600 px-2 py-1.5 rounded-lg text-sm hover:bg-gray-300">✕</button>
                    </div>
                  ) : (
                    <button onClick={() => { setCrediting(u.id); setResult(null); }}
                      className="text-sm bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-100 font-medium">
                      💰 Add Funds
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick demo funding shortcuts */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">⚡ Quick Demo Funding</h3>
        <p className="text-sm text-blue-600 mb-3">Standard demo amounts for YC presentation</p>
        <div className="flex gap-3 flex-wrap">
          {[
            { label: "$500 CAD", amount: 500, currency: "CAD", reason: "YC demo funding" },
            { label: "$1,000 CAD", amount: 1000, currency: "CAD", reason: "YC demo funding" },
            { label: "$250 USD", amount: 250, currency: "USD", reason: "YC demo funding" },
          ].map(preset => (
            <button key={preset.label}
              onClick={() => { setAmount(preset.amount.toString()); setCurrency(preset.currency); setReason(preset.reason); }}
              className="px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
              {preset.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-blue-400 mt-2">Select a preset then click "Add Funds" on the desired user</p>
      </div>
    </div>
  );
}
